import { supabase } from '@/lib/supabase'
import { deleteCookie } from '@/lib/cookies'
import type { CurrentUserProfile } from '@/types'
import { IMPERS_STASH_COOKIE, IMPERS_ACTIVE_TAB_KEY } from '../lib/storage-keys'
import type { ImpersonationAdminStash } from '../lib/types'

/**
 * God mode: platform admin "entra" num tenant como si mesmo.
 * Fluxo:
 *  1. RPC enter_admin_tenant atualiza users.tenant_id + audit log
 *  2. refreshSession() re-minta o JWT com novo tenant_id no claim
 *  3. Abre tab nova com ?tenant={id} — marcador pra o gate no app principal
 *     liberar acesso (sem param = redirect pro admin panel)
 *
 * Defense-in-depth: limpa qualquer resíduo de impersonação ANTES de entrar em
 * god mode. O cookie IMPERS_STASH_COOKIE é cross-subdomain (.runnaflow.com), e
 * a flag IMPERS_ACTIVE_TAB_KEY em sessionStorage é herdada pela aba filha
 * quando aberta via window.open com mesma origem. Sem esse cleanup, se o user
 * tentou impersonar antes (mesmo falhando), a aba do god mode lê os resíduos
 * e cai no branch de impersonação, mostrando ImpersonationBanner em vez de
 * GodModeBanner.
 */
export async function enterAdminTenant(params: {
  tenantId: string
  profile: CurrentUserProfile
  setImpersStash: (s: ImpersonationAdminStash | null) => void
}): Promise<void> {
  const { tenantId, profile, setImpersStash } = params

  if (!profile.is_platform_admin) throw new Error('Somente platform admin')

  deleteCookie(IMPERS_STASH_COOKIE)
  try { sessionStorage.removeItem(IMPERS_ACTIVE_TAB_KEY) } catch { /* ignore */ }
  setImpersStash(null)

  const { error: rpcErr } = await supabase.rpc('enter_admin_tenant', { p_tenant_id: tenantId })
  if (rpcErr) throw new Error(`enter_admin_tenant: ${rpcErr.message}`)
  const { error: refreshErr } = await supabase.auth.refreshSession()
  if (refreshErr) throw new Error(`refreshSession: ${refreshErr.message}`)

  const host = window.location.hostname
  const appOrigin =
    host === 'admin.runnaflow.com'      ? 'https://runnaflow.com' :
    host === 'admin-dev.runnaflow.com'  ? 'https://runnaflow-dev.runnaflow.com' :
    window.location.origin
  window.open(`${appOrigin}/?tenant=${tenantId}`, '_blank')
}
